import { useState, useCallback, useRef, useEffect } from 'react'
import VoiceButton from '../components/VoiceButton'
import type { Message } from '../lib/types'
import GoogleSignIn from '../components/GoogleSignIn'
import Receipt from '../components/Receipt'
import RotatingHints from '../components/RotatingHints'
import SettingsDrawer from '../components/SettingsDrawer'
import { useVoice } from '../hooks/useVoice'
import { useLiveTranscript } from '../hooks/useLiveTranscript'
import { useSettings } from '../hooks/useSettings'
import { useMediaQuery } from '../hooks/useMediaQuery'
import {
  sendVoiceMessage,
  finalizeSession,
  sendTextMessage,
  speak,
  stopSpeaking,
  warmUpTTS,
  type ExtractedItem,
} from '../lib/api'
import { uploadPhoto } from '../lib/photo'

const INITIAL_GREETING = "Hi, what did you find? I'll help log it so the owner can get it back."
const SESSION_KEY = 'tlc_venue_session'
// Tightened from 24h → 20min — see Home.tsx loadSession comment.
const SESSION_MAX_AGE = 20 * 60 * 1000

function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (Date.now() - data.savedAt > SESSION_MAX_AGE) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    if (data.isComplete) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    if (!data.messages || data.messages.length === 0) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    return data
  } catch { return null }
}

interface VenueProps {
  onRecordingChange?: (recording: boolean) => void
}

export default function Venue({ onRecordingChange }: VenueProps) {
  const voice = useVoice()
  const { interim: liveTranscript, supported: liveSupported } = useLiveTranscript(
    voice.state === 'recording'
  )
  const saved = useRef(loadSession())

  const [sessionId, setSessionId] = useState<string | undefined>(saved.current?.sessionId)
  const [itemId, setItemId] = useState<string | undefined>(saved.current?.itemId)
  const [messages, setMessages] = useState<Message[]>(
    saved.current?.messages ?? []
  )
  const [extractedItem, setExtractedItem] = useState<Partial<ExtractedItem>>(saved.current?.extractedItem ?? {})
  const [referenceCode, setReferenceCode] = useState<string | undefined>(saved.current?.referenceCode)
  // Rehydrate isComplete so a completed session self-evicts on next load
  // (the clear-on-complete effect below fires immediately when this is true).
  const [isComplete, setIsComplete] = useState(saved.current?.isComplete ?? false)
  const [resumed, setResumed] = useState(!!saved.current?.sessionId && !saved.current?.isComplete)
  const [textInput, setTextInput] = useState('')
  const [textSending, setTextSending] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settings] = useSettings()
  // Type-by-default on desktop (≥900px). See Home.tsx comment.
  const isDesktop = useMediaQuery('(min-width: 900px)')
  const showTextInput = settings.textInputEnabled || isDesktop
  const msgCounter = useRef(saved.current?.messages?.length ?? 1)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const nextId = () => `msg-${++msgCounter.current}`

  // Persist session to localStorage
  useEffect(() => {
    if (!sessionId) return
    localStorage.setItem(SESSION_KEY, JSON.stringify({
      sessionId, itemId, messages, extractedItem, referenceCode, isComplete, savedAt: Date.now(),
    }))
  }, [sessionId, itemId, messages, extractedItem, referenceCode, isComplete])

  // Clear session on completion
  useEffect(() => {
    if (isComplete) localStorage.removeItem(SESSION_KEY)
  }, [isComplete])

  // Notify parent of recording state changes
  useEffect(() => {
    onRecordingChange?.(voice.state === 'recording')
  }, [voice.state, onRecordingChange])

  const handleResponse = useCallback(
    (data: {
      transcript?: string
      response: string
      session_id: string
      item_id?: string
      extracted_item: Partial<ExtractedItem>
      is_complete: boolean
      reference_code?: string
    }) => {
      setSessionId(data.session_id)
      if (data.item_id) setItemId(data.item_id)

      const newMessages: Message[] = []
      if (data.transcript) {
        newMessages.push({ id: nextId(), role: 'user', text: data.transcript })
      }
      newMessages.push({ id: nextId(), role: 'agent', text: data.response })
      setMessages((prev) => [...prev, ...newMessages])

      setExtractedItem((prev) => ({ ...prev, ...data.extracted_item }))

      if (data.reference_code) setReferenceCode(data.reference_code)
      if (data.is_complete) setIsComplete(true)

      speak(data.response)
    },
    []
  )

  const handleStart = useCallback(() => {
    warmUpTTS()
    stopSpeaking()
    voice.startRecording().catch(() => {
      setMessages(prev => [...prev, {
        id: nextId(),
        role: 'agent',
        text: "I need microphone access to listen. Please allow it in your browser settings, or use the text input below."
      }])
    })
  }, [voice])

  const handleStop = useCallback(async () => {
    // Optimistic Web Speech reverted — Whisper is the source of truth.
    try {
      const blob = await voice.stopRecording()
      const data = await sendVoiceMessage(blob, sessionId, 'found')
      handleResponse(data)
    } catch (err) {
      console.error('Voice send failed:', err)
      const text = (err instanceof TypeError && err.message.includes('fetch'))
        ? "Can't reach the server. Check your connection and try again."
        : "Sorry, I couldn't catch that. Could you try again?"
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'agent', text },
      ])
    } finally {
      voice.setIdle()
    }
  }, [voice, sessionId, handleResponse])

  const handleTextSend = useCallback(async () => {
    const text = textInput.trim()
    if (!text || textSending) return

    warmUpTTS()
    setTextInput('')
    setTextSending(true)
    voice.setProcessing()
    setMessages((prev) => [...prev, { id: nextId(), role: 'user', text }])

    try {
      const data = await sendTextMessage(text, sessionId, 'found')
      handleResponse({ ...data, transcript: undefined })
    } catch (err) {
      console.error('Text send failed:', err)
      const msg = (err instanceof TypeError && err.message.includes('fetch'))
        ? "Can't reach the server. Check your connection and try again."
        : 'Something went wrong. Please try again.'
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'agent', text: msg },
      ])
    } finally {
      setTextSending(false)
      voice.setIdle()
    }
  }, [textInput, textSending, sessionId, handleResponse, voice])

  const handlePhotoUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file || !itemId) return

      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'user', text: `[Photo: ${file.name}]` },
      ])

      try {
        const photoUrls = await uploadPhoto(itemId, file)
        setExtractedItem(prev => ({ ...prev, photo_urls: photoUrls }))
        setMessages((prev) => [
          ...prev,
          { id: nextId(), role: 'agent', text: 'Photo uploaded successfully.' },
        ])
      } catch (err) {
        console.error('Photo upload failed:', err)
        setMessages((prev) => [
          ...prev,
          { id: nextId(), role: 'agent', text: "Couldn't upload the photo. Please try again." },
        ])
      }

      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [itemId]
  )

  const handleNewReport = useCallback(() => {
    localStorage.removeItem(SESSION_KEY)
    setSessionId(undefined)
    setItemId(undefined)
    setMessages([])
    setExtractedItem({})
    setReferenceCode(undefined)
    setIsComplete(false)
    setResumed(false)
    setTextInput('')
    msgCounter.current = 1
    stopSpeaking()
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!sessionId || submitting) return
    if (!confirm('Log this found item with what you\'ve shared so far?')) return
    setSubmitting(true)
    stopSpeaking()
    try {
      const data = await finalizeSession(sessionId)
      setReferenceCode(data.reference_code)
      setIsComplete(true)
    } catch (err) {
      console.error('Submit failed:', err)
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'agent', text: "Couldn't submit just now — please try again." },
      ])
    } finally {
      setSubmitting(false)
    }
  }, [sessionId, submitting])

  const preInteraction = messages.length === 0 && voice.state === 'idle' && !isComplete

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div className="page-bloom" aria-hidden="true" />

      <button
        onClick={() => setSettingsOpen(true)}
        className="absolute left-4 top-4 z-20 p-2 rounded-full hover:bg-[var(--surface-2)] transition-colors"
        style={{ color: 'var(--ink-4)' }}
        aria-label="Settings"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      <span
        className="absolute right-4 top-4 z-20 text-[10px] font-medium tracking-[0.18em] uppercase"
        style={{ color: 'var(--ink-4)' }}
      >
        Venue
      </span>

      {sessionId && (
        <button
          onClick={handleNewReport}
          className="absolute right-4 top-12 z-20 px-3 py-1.5 rounded-full text-xs font-medium hover:bg-[var(--surface-2)] transition-colors"
          style={{ color: 'var(--ink-3)' }}
        >
          New
        </button>
      )}

      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {resumed && (
        <div className="relative z-10 shrink-0 mx-4 mt-14 px-3 py-2 rounded-md border text-xs flex items-center justify-between gap-3"
             style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-2)', color: 'var(--ink-2)' }}>
          <span>Resuming your previous report.</span>
          <button
            onClick={handleNewReport}
            className="text-xs font-medium underline underline-offset-2 hover:text-[var(--ink-1)] transition-colors"
          >
            Start new
          </button>
        </div>
      )}

      <div className="relative z-10 flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
          {preInteraction ? (
            <div className="min-h-full flex flex-col items-center justify-center text-center px-6 pb-4">
              <h1
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'var(--text-display)',
                  fontWeight: 500,
                  lineHeight: 1.05,
                  letterSpacing: '-0.015em',
                  color: 'var(--ink-1)',
                }}
              >
                tell me<br />what you found
              </h1>
              <p className="mt-6 text-[17px] leading-relaxed" style={{ color: 'var(--ink-2)' }}>
                the what, when &amp; where
              </p>
            </div>
          ) : isComplete && referenceCode ? (
            <div className="min-h-full flex flex-col items-center justify-center px-4 py-8">
              <div className="text-center animate-fade-in-scale">
                <div className="inline-flex items-center gap-2 rounded-full px-5 py-2.5" style={{ background: 'rgba(63, 143, 74, 0.08)', border: '1px solid rgba(63, 143, 74, 0.2)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-[var(--success)]">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span className="text-[var(--success)] text-sm font-medium">Item logged</span>
                </div>
                <p className="mt-3 text-sm" style={{ color: 'var(--ink-2)' }}>
                  Reference: <span className="font-mono" style={{ color: 'var(--accent)' }}>{referenceCode}</span>
                </p>
                <GoogleSignIn itemId={itemId} />
              </div>
            </div>
          ) : (
            <div className="pt-20 pb-4">
              <Receipt
                messages={messages}
                voiceState={voice.state}
                item={extractedItem}
                reportType="found"
              />
            </div>
          )}
        </div>

        {!isComplete && (
          <div className="shrink-0 flex flex-col items-center gap-2 px-4 pt-2 pb-3">
            {voice.state === 'recording' && (
              <div
                className="px-4 max-w-md text-center min-h-[1.5rem]"
                style={{
                  color: 'var(--ink-2)',
                  fontFamily: 'var(--font-serif)',
                  fontStyle: 'italic',
                  fontSize: '17px',
                  lineHeight: 1.5,
                }}
                aria-live="polite"
              >
                {liveSupported
                  ? (liveTranscript || <span style={{ color: 'var(--ink-4)' }}>…listening</span>)
                  : <span style={{ color: 'var(--ink-4)' }}>recording…</span>}
              </div>
            )}
            <VoiceButton state={voice.state} onStart={handleStart} onStop={handleStop} />
            {voice.state === 'idle' && !sessionId && (
              <RotatingHints reportType="found" item={extractedItem} active={true} />
            )}
            {voice.state !== 'recording' && (
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="
                    flex items-center gap-1.5 px-3.5 py-2 rounded-full
                    bg-[var(--surface-2)] border border-[var(--border-subtle)]
                    text-sm text-[var(--ink-2)] hover:text-[var(--ink-1)] hover:border-[var(--border-default)]
                    transition-colors
                  "
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                  Add photo
                </button>
                {itemId && (
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="
                      px-5 py-2 rounded-full text-sm font-medium
                      bg-[var(--accent)] text-white
                      hover:opacity-90 active:opacity-80
                      disabled:opacity-50 disabled:cursor-not-allowed
                      transition-opacity
                    "
                  >
                    {submitting ? 'Logging…' : 'Log Item'}
                  </button>
                )}
              </div>
            )}
            {showTextInput && voice.state !== 'recording' && (
              <div className="w-full max-w-md mt-1 flex gap-2">
                <input
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleTextSend()}
                  placeholder="Or type your message…"
                  disabled={textSending}
                  className="
                    flex-1 bg-[var(--surface-1)] border border-[var(--border-subtle)] rounded-full px-4 py-2
                    text-sm text-[var(--ink-1)] placeholder-[var(--ink-4)]
                    focus:outline-none focus:border-[var(--border-accent)] focus:ring-2 focus:ring-[var(--accent-glow)]
                    transition-colors
                  "
                />
                <button
                  onClick={handleTextSend}
                  disabled={!textInput.trim() || textSending}
                  className="
                    px-4 py-2 rounded-full text-sm font-medium
                    disabled:opacity-30 disabled:cursor-not-allowed
                    transition-colors hover:brightness-110
                  "
                  style={{ background: 'var(--accent)', color: 'var(--text-on-accent)' }}
                >
                  Send
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
